import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import MenuItem from '@mui/material/MenuItem';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLongOutlined';
import PersonIcon from '@mui/icons-material/PersonOutline';
import ListAltIcon from '@mui/icons-material/ListAltOutlined';
import {
  billUrl,
  billTypeUrl,
  clientUrl,
  productUrl,
  saleTypeUrl,
} from '../../../constants/urls';
import { formatCurrency } from '../../../utils/currency';
import { formatDate } from '../../../utils/date';
import { formatCuit } from '../../../utils/cuit';
import Toast from '../../../components/Toast';
import { useToast } from '../../../context/ToastContext';
import CustomInput from '../../../components/Input';
import AmountInput from '../../../components/AmountInput';
import CountUp from '../../../components/CountUp';
import FormActions from '../../../components/FormActions';
import BackButton from '../../../components/BackButton';
import { TUTORIAL_EVENTS } from '../../../constants/tutorials';
import { emitTutorialEvent } from '../../../utils/tutorial';

const DEBIT_NOTE_CODES = new Set([2, 7, 12, 52]);
const CREDIT_NOTE_CODES = new Set([3, 8, 13, 53]);
const MANUAL_PRICE_CODES = new Set([...DEBIT_NOTE_CODES, ...CREDIT_NOTE_CODES]);
const DEBIT_NOTE_TO_INVOICE_CODE = { 2: 1, 7: 6, 12: 11, 52: 51 };
const CREDIT_NOTE_TO_INVOICE_CODE = { 3: 1, 8: 6, 13: 11, 53: 51 };

const EMPTY_ITEM = {
  producto: null,
  cantidad: '',
  tipo_venta: null,
  precio_aplicado: '',
  iva_rate: '10.5',
};

const IVA_OPTIONS = ['0', '2.5', '5', '10.5', '21', '27'];

// ── Estilos reutilizables ──────────────────────────────────────────────────────
const inputCls = (hasError, isLocked = false) =>
  isLocked
    ? 'w-full bg-field-locked px-3 py-2.5 rounded-lg border border-field-locked-border text-sm text-on-surface-muted cursor-not-allowed'
    : `w-full bg-surface-low px-3 py-2.5 rounded-lg border text-sm text-on-surface placeholder:text-gray-400 focus:outline-none focus:ring-2 transition-all ${
        hasError
          ? 'border-red-400 ring-2 ring-red-100'
          : 'border-border-subtle focus:border-blue-lahuerta/40 focus:ring-blue-lahuerta/10'
      }`;

const labelCls = 'block text-[0.6875rem] font-bold text-on-surface-muted uppercase tracking-wider mb-1.5';

const colsMap = {
  1: 'md:grid-cols-1',
  2: 'md:grid-cols-2',
  3: 'md:grid-cols-3',
  4: 'md:grid-cols-4',
};

const SectionCard = ({ icon, title, children, cols = 3 }) => (
  <section className="space-y-3">
    <div className="flex items-center gap-2 px-1">
      <span className="text-blue-lahuerta">{icon}</span>
      <h2 className="text-base font-semibold text-on-surface">{title}</h2>
    </div>
    <div className={`bg-surface-card p-6 rounded-xl shadow-sm border border-border-subtle grid grid-cols-1 ${colsMap[cols] ?? 'md:grid-cols-3'} gap-6`}>
      {children}
    </div>
  </section>
);

const FieldError = ({ message }) =>
  message ? <p className="mt-1 text-xs text-red-500">{message}</p> : null;

// sx compartido para MUI Autocomplete
const autocompleteSx = (hasError) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'var(--color-surface-low)',
    borderRadius: '0.5rem',
    minHeight: '2.75rem', // mismo alto que los inputs y selects del formulario
    fontSize: '0.875rem',
    padding: '0 !important',
    '& fieldset': {
      borderColor: hasError ? '#f87171' : 'var(--color-border-subtle)',
    },
    '&:hover fieldset': {
      borderColor: hasError ? '#f87171' : '#4a7bc4',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#4a7bc4',
      borderWidth: '1px',
    },
  },
  '& .MuiInputBase-input': {
    padding: '0.625rem 0.75rem !important',
    fontSize: '0.875rem',
    color: 'var(--color-on-surface)',
  },
});

// Normaliza para buscar sin distinguir mayúsculas ni acentos.
const normalizeText = (text = '') =>
  text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

// Filtra clientes por razón social, nombre de fantasía o CUIT (con o sin guiones).
const filterClients = (options, { inputValue }) => {
  const term = normalizeText(inputValue).trim();
  if (!term) return options;

  const isCuitSearch = /^[\d\s-]+$/.test(term);
  const cuitDigits = term.replace(/\D/g, '');

  return options.filter((client) =>
    normalizeText(client.razon_social).includes(term) ||
    normalizeText(client.nombre_fantasia || '').includes(term) ||
    (isCuitSearch && client.cuit.includes(cuitDigits))
  );
};

// ── Componente principal ───────────────────────────────────────────────────────
const FacturaForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [clients, setClients] = useState([]);
  const [billTypes, setBillTypes] = useState([]);
  const [products, setProducts] = useState([]);
  const [saleTypes, setSaleTypes] = useState([]);
  const [clientPrices, setClientPrices] = useState({});

  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedBillType, setSelectedBillType] = useState(null);
  const [fecha, setFecha] = useState(new Date().toISOString().split('T')[0]);

  const [items, setItems] = useState([{ ...EMPTY_ITEM }]);

  const [associatedBill, setAssociatedBill] = useState(null);
  const [associatedBillOptions, setAssociatedBillOptions] = useState([]);
  const [loadMode, setLoadMode] = useState(null); // null | 'all' | 'individual'

  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState({ open: false, message: '' });
  const { showToast } = useToast();

  const isDebitNote = DEBIT_NOTE_CODES.has(selectedBillType?.codigo_afip);
  const isCreditNote = CREDIT_NOTE_CODES.has(selectedBillType?.codigo_afip);
  const isNoteType = isDebitNote || isCreditNote;
  const clientWithoutPriceList = Boolean(selectedClient) && !selectedClient.lista_precios;
  const isManualPrice = MANUAL_PRICE_CODES.has(selectedBillType?.codigo_afip) || clientWithoutPriceList;
  const isElectronic = selectedBillType?.codigo_afip != null;

  // ── Load options ───────────────────────────────────────────────────
  useEffect(() => {
    const loadOptions = async () => {
      const [clientsResponse, billTypesResponse, productsResponse, saleTypesResponse] = await Promise.all([
        axios.get(clientUrl),
        axios.get(billTypeUrl),
        axios.get(productUrl),
        axios.get(saleTypeUrl),
      ]);

      setClients(
        clientsResponse.data.map((client) => ({
          label: `${client.cuit} - ${client.razon_social}`,
          ...client,
        }))
      );

      const mappedBillTypes = billTypesResponse.data.map((billType) => ({
        label: billType.descripcion,
        ...billType,
      }));
      setBillTypes(mappedBillTypes);

      setProducts(
        productsResponse.data.map((product) => ({
          label: product.descripcion,
          ...product,
        }))
      );

      setSaleTypes(saleTypesResponse.data);

      if (isEdit) return;
      setSelectedBillType(null);
    };

    loadOptions().catch(console.error);
  }, [isEdit]);

  // ── Precargar tipo de factura según condición IVA del cliente ─────────
  useEffect(() => {
    if (isEdit || !selectedClient || billTypes.length === 0) return;

    const ivaCode = selectedClient.condicion_IVA?.codigo_afip;
    const targetAbr = [1, 6].includes(ivaCode) ? 'A' : 'B';
    const billType = billTypes.find((bt) => bt.abreviatura === targetAbr);
    if (billType) setSelectedBillType(billType);
  }, [selectedClient, billTypes, isEdit]);

  // ── Reset load mode when associated bill or manual-price mode changes ─
  useEffect(() => {
    setLoadMode(null);
    if (!isManualPrice) setItems([{ ...EMPTY_ITEM }]);
  }, [associatedBill, isManualPrice]);

  // ── Load associated bill options (for debit notes and credit notes) ──
  useEffect(() => {
    if (!isNoteType || !selectedClient) {
      setAssociatedBill(null);
      setAssociatedBillOptions([]);
      return;
    }

    const mapping = isDebitNote ? DEBIT_NOTE_TO_INVOICE_CODE : CREDIT_NOTE_TO_INVOICE_CODE;
    const invoiceCode = mapping[selectedBillType.codigo_afip];
    const invoiceType = billTypes.find((bt) => bt.codigo_afip === invoiceCode);
    if (!invoiceType) return;

    axios
      .get(billUrl, { params: { client_id: selectedClient.id, bill_type_id: invoiceType.id } })
      .then((res) => {
        setAssociatedBillOptions(
          res.data
            .filter((b) => b.cae)
            .map((b) => ({
              label: `${b.tipo_factura.descripcion} N°${b.numero_comprobante} — ${formatCurrency(b.total)} — ${formatDate(b.fecha)}`,
              ...b,
            }))
        );
      })
      .catch(() => setAssociatedBillOptions([]));
  }, [isNoteType, isDebitNote, selectedClient, selectedBillType, billTypes]);

  // ── Load client prices ─────────────────────────────────────────────
  useEffect(() => {
    if (!selectedClient?.lista_precios) {
      setClientPrices({});
      return;
    }

    axios
      .get(`${clientUrl}${selectedClient.id}/products-with-prices/`)
      .then((response) => {
        const priceMap = {};

        response.data.forEach((entry) => {
          const productId = Number(entry.producto.id);
          const saleTypeId = Number(entry.tipo_venta.id);
          const price = Number(entry.precio);

          if (!priceMap[productId]) priceMap[productId] = {};
          priceMap[productId][saleTypeId] = price;
        });

        setClientPrices(priceMap);
      })
      .catch((error) => {
        console.error('ERROR LOADING PRICES:', error);
        setClientPrices({});
      });
  }, [selectedClient]);

  // ── Load edit data ─────────────────────────────────────────────────
  useEffect(() => {
    if (!isEdit) return;

    axios
      .get(`${billUrl}${id}/`)
      .then((response) => {
        const bill = response.data;

        setFecha(bill.fecha);
        setSelectedClient({
          label: `${bill.cliente.cuit} - ${bill.cliente.razon_social}`,
          ...bill.cliente,
        });
        setSelectedBillType({
          label: bill.tipo_factura.descripcion,
          ...bill.tipo_factura,
        });

        setItems(
          bill.items.map((item) => ({
            producto: { label: item.producto.descripcion, ...item.producto },
            cantidad: item.cantidad,
            tipo_venta: item.tipo_venta?.id ?? null,
            precio_aplicado: item.precio_aplicado,
            iva_rate: String(parseFloat(item.iva_rate ?? '10.5')),
          }))
        );
      })
      .catch(console.error);
  }, [id, isEdit]);

  // ── Load mode handlers ────────────────────────────────────────────
  const handleLoadAll = () => {
    setItems([
      ...associatedBill.items.map((item) => ({
        producto: { label: item.producto.descripcion, ...item.producto },
        cantidad: item.cantidad,
        tipo_venta: item.tipo_venta?.id ?? null,
        precio_aplicado: item.precio_aplicado,
        iva_rate: String(parseFloat(item.iva_rate ?? '10.5')),
      })),
      { ...EMPTY_ITEM },
    ]);
    setLoadMode('all');
  };

  const handleLoadIndividual = () => {
    setItems([{ ...EMPTY_ITEM }]);
    setLoadMode('individual');
  };

  // ── Item helpers ───────────────────────────────────────────────────
  const addItem = () => setItems((previous) => [...previous, { ...EMPTY_ITEM }]);

  const removeItem = (index) =>
    setItems((previous) => previous.filter((_, currentIndex) => currentIndex !== index));

  const updateItem = useCallback((index, field, value) => {
    setItems((previous) => {
      const nextItems = [...previous];
      nextItems[index] = { ...nextItems[index], [field]: value };
      return nextItems;
    });
  }, []);

  const resolvePrice = useCallback(
    (productId, saleTypeId) => {
      if (!productId || !saleTypeId) return '';
      const price = clientPrices[productId]?.[saleTypeId];
      return price ?? '';
    },
    [clientPrices]
  );

  const handleProductSelect = (index, product) => {
    if (!product) {
      setItems((previous) => {
        const nextItems = [...previous];
        nextItems[index] = { ...EMPTY_ITEM };
        return nextItems;
      });
      return;
    }

    setItems((previous) => {
      const nextItems = [...previous];
      const saleTypeId = nextItems[index].tipo_venta;
      const price = isManualPrice ? '' : resolvePrice(product.id, saleTypeId);

      nextItems[index] = {
        ...nextItems[index],
        producto: product,
        precio_aplicado: price,
      };

      return nextItems;
    });
  };

  const handleSaleTypeChange = (index, saleTypeId) => {
    setItems((previous) => {
      const nextItems = [...previous];
      const productId = nextItems[index].producto?.id;
      const price = isManualPrice ? nextItems[index].precio_aplicado : resolvePrice(productId, saleTypeId);

      nextItems[index] = {
        ...nextItems[index],
        tipo_venta: saleTypeId,
        precio_aplicado: price,
      };

      return nextItems;
    });
  };

  const hasDuplicatedProducts = () => {
    const selectedIds = items.map((item) => item.producto?.id).filter(Boolean);
    return new Set(selectedIds).size !== selectedIds.length;
  };

  const getAvailableProducts = (currentIndex) => {
    const selectedProductIds = items
      .filter((_, index) => index !== currentIndex)
      .map((item) => item.producto?.id)
      .filter(Boolean);

    return products.filter((product) => !selectedProductIds.includes(product.id));
  };

  // ── Totals ─────────────────────────────────────────────────────────
  const calculateSubtotal = (item) => {
    if (!item.tipo_venta || !item.precio_aplicado) return null;
    const quantity = parseFloat(item.cantidad) || 0;
    const price = parseFloat(item.precio_aplicado) || 0;
    return quantity * price;
  };

  const calculateItemTotal = (item) => {
    const net = calculateSubtotal(item);
    if (net === null) return null;
    if (!isElectronic) return net;
    return net * (1 + parseFloat(item.iva_rate || 10.5) / 100);
  };

  const total = items.reduce((sum, item) => sum + (calculateItemTotal(item) ?? 0), 0);

  // Línea con producto, cantidad, tipo de venta y (si es precio manual) precio.
  const isItemComplete = (item) => {
    const baseReady = Boolean(item.producto) && parseFloat(item.cantidad) > 0 && Boolean(item.tipo_venta);
    const priceReady = isManualPrice ? parseFloat(item.precio_aplicado) > 0 : true;
    return baseReady && priceReady;
  };

  // ── Auto-add row ───────────────────────────────────────────────────
  useEffect(() => {
    if (isItemComplete(items[items.length - 1])) {
      setItems((previous) => [...previous, { ...EMPTY_ITEM }]);
    }
  }, [items, isManualPrice]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Validation ─────────────────────────────────────────────────────
  const validate = () => {
    const nextErrors = {};

    if (!selectedClient) nextErrors.client = 'Debe seleccionar un cliente';
    if (!selectedBillType) nextErrors.billType = 'Debe seleccionar un tipo de factura';
    if (!fecha) nextErrors.fecha = 'Debe ingresar una fecha';
    if (isNoteType && !associatedBill) nextErrors.associatedBill = 'Debe seleccionar la factura a asociar';

    const filledItems = items.filter((item) => item.producto !== null);

    if (filledItems.length === 0) {
      nextErrors.items_empty = 'Debe agregar al menos un producto';
      return nextErrors;
    }

    items.forEach((item, index) => {
      if (!item.producto) return;

      if (!item.cantidad || parseFloat(item.cantidad) <= 0) {
        nextErrors[`item_${index}_cantidad`] = 'Requerido';
      }

      if (!item.tipo_venta) {
        nextErrors[`item_${index}_tipo_venta`] = 'Requerido';
      }

      if (isManualPrice && (!item.precio_aplicado || parseFloat(item.precio_aplicado) <= 0)) {
        nextErrors[`item_${index}_precio`] = 'Requerido';
      }
    });

    if (hasDuplicatedProducts()) {
      nextErrors.items_duplicated =
        'No se puede agregar el mismo producto más de una vez en la factura';
    }

    return nextErrors;
  };

  // ── Save ───────────────────────────────────────────────────────────
  const handleSave = async () => {
    const validationErrors = validate();

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    setSaving(true);

    try {
      const payload = {
        cliente: selectedClient.id,
        tipo_factura: selectedBillType.id,
        fecha,
        items: items
          .filter((item) => item.producto !== null)
          .map((item) => ({
            producto: item.producto.id,
            cantidad: item.cantidad,
            tipo_venta: item.tipo_venta,
            ...(isManualPrice && { precio_aplicado: item.precio_aplicado }),
            ...(isElectronic && { iva_rate: item.iva_rate || '10.5' }),
          })),
        ...(associatedBill && { factura_asociada: associatedBill.id }),
      };

      let response;
      if (isEdit) {
        response = await axios.put(`${billUrl}${id}/`, payload);
      } else {
        response = await axios.post(billUrl, payload);
        emitTutorialEvent(TUTORIAL_EVENTS.billCreated, { id: response.data.id });
      }

      const receipt = `${selectedBillType.label} N° ${response.data.numero_comprobante}`;
      showToast(isEdit ? `${receipt} se actualizó correctamente.` : `${receipt} se generó correctamente.`);
      navigate(`/bill/detail/${response.data.id}`);
    } catch (error) {
      const message =
        error?.response?.data?.detail ||
        error?.response?.data ||
        'Error al guardar la factura.';

      setToast({ open: true, message: typeof message === 'string' ? message : JSON.stringify(message) });
    } finally {
      setSaving(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────
  return (
    <div className="w-full max-w-7xl mx-auto space-y-8 pb-12">
      <Toast
        open={toast.open}
        message={toast.message}
        onClose={() => setToast({ open: false, message: '' })}
      />
      {/* Breadcrumbs + Volver arriba a la derecha, separados del contenido con la misma línea que el pie */}
      <div className="flex items-center justify-between gap-4 pb-4 border-b border-border-subtle">
        <nav className="flex items-center flex-wrap gap-2 text-sm font-medium text-on-surface-muted">
          <span
            className="whitespace-nowrap hover:text-accent cursor-pointer transition-colors"
            onClick={() => navigate('/')}
          >
            Home
          </span>
          <span className="text-xs">›</span>
          <span
            className="whitespace-nowrap hover:text-accent cursor-pointer transition-colors"
            onClick={() => navigate('/bill')}
          >
            Facturas
          </span>
          <span className="text-xs">›</span>
          <span className="text-on-surface font-semibold">
            {isEdit ? `Editar #${String(id).padStart(8, '0')}` : 'Nueva'}
          </span>
        </nav>
        <BackButton onClick={() => navigate('/bill')} className="shrink-0 px-5 py-2.5 text-sm" />
      </div>

      {/* 1. Datos de la factura */}
      <SectionCard icon={<ReceiptLongIcon sx={{ fontSize: 20 }} />} title="Datos de la Factura" cols={2}>
        <div className="flex flex-col gap-1" data-tour="bill-client" data-tour-complete={Boolean(selectedClient)}>
          <label className={labelCls}>Cliente *</label>
          <Autocomplete
            options={clients}
            value={selectedClient}
            onChange={(_, client) => {
              const priceModeChanged = Boolean(client?.lista_precios) !== Boolean(selectedClient?.lista_precios);
              if (!isNoteType && priceModeChanged) setItems([{ ...EMPTY_ITEM }]);
              setSelectedClient(client);
            }}
            filterOptions={filterClients}
            getOptionLabel={(client) => client.label}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            noOptionsText="No se encontraron clientes"
            size="small"
            renderOption={(props, client) => (
              <li {...props} key={client.id}>
                <div className="flex flex-col">
                  <span className="text-sm">{client.razon_social}</span>
                  <span className="text-xs opacity-70">
                    {formatCuit(client.cuit)}{client.nombre_fantasia ? ` · ${client.nombre_fantasia}` : ''}
                  </span>
                </div>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Buscar por CUIT, razón social o nombre de fantasía..."
                size="small"
                sx={autocompleteSx(Boolean(errors.client))}
              />
            )}
          />
          <FieldError message={errors.client} />
        </div>

        {/* Fecha */}
        <div className="flex flex-col gap-1" data-tour="bill-date">
          <label className={labelCls}>Fecha *</label>
          <input
            type="date"
            value={fecha}
            onChange={(event) => setFecha(event.target.value)}
            className={inputCls(Boolean(errors.fecha))}
          />
          <FieldError message={errors.fecha} />
        </div>

        {/* Tipo de facturación */}
        <div className="flex flex-col gap-1" data-tour="bill-type" data-tour-complete={Boolean(selectedBillType)}>
          <label className={labelCls}>Tipo de facturación *</label>
          <Autocomplete
            options={billTypes}
            value={selectedBillType}
            onChange={(_, billType) => {
              const noteTypeChanged = MANUAL_PRICE_CODES.has(billType?.codigo_afip) !== isNoteType;
              if (noteTypeChanged) setItems([{ ...EMPTY_ITEM }]);
              setSelectedBillType(billType);
              setAssociatedBill(null);
            }}
            getOptionLabel={(billType) => billType.label}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            noOptionsText="No se encontraron tipos de facturación"
            size="small"
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Buscar tipo de facturación..."
                size="small"
                sx={autocompleteSx(Boolean(errors.billType))}
              />
            )}
          />
          <FieldError message={errors.billType} />
        </div>

        {/* Factura asociada — para Notas de Débito y Notas de Crédito */}
        {isNoteType && (
          <div className="flex flex-col gap-1">
            <label className={labelCls}>Factura asociada *</label>
            <select
              value={associatedBill?.id || ''}
              onChange={(e) => {
                const bill = associatedBillOptions.find((b) => String(b.id) === e.target.value) || null;
                setAssociatedBill(bill);
              }}
              className={inputCls(Boolean(errors.associatedBill), !selectedClient)}
              disabled={!selectedClient}
            >
              <option value="">
                {selectedClient ? 'Seleccionar factura...' : 'Seleccioná un cliente primero'}
              </option>
              {associatedBillOptions.length === 0 && selectedClient && (
                <option disabled>Sin facturas emitidas para este cliente</option>
              )}
              {associatedBillOptions.map((b) => (
                <option key={b.id} value={b.id}>{b.label}</option>
              ))}
            </select>
            <FieldError message={errors.associatedBill} />
          </div>
        )}
      </SectionCard>

      {/* 2. Prompt de carga — solo para ND/NC con factura asociada seleccionada */}
      {isNoteType && associatedBill && loadMode === null && (
        <div className="bg-surface-card border border-border-subtle rounded-xl shadow-sm p-6 flex flex-col gap-4">
          <p className="text-sm font-semibold text-on-surface">
            ¿Cómo querés cargar los productos?
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              onClick={handleLoadAll}
              className="flex-1 px-5 py-3 bg-blue-lahuerta text-white text-sm font-semibold rounded-lg hover:bg-blue-lahuerta/90 transition-colors"
            >
              Cargar todos los productos de la factura original
            </button>
            <button
              type="button"
              onClick={handleLoadIndividual}
              className="flex-1 px-5 py-3 border border-border-subtle text-on-surface text-sm font-semibold rounded-lg hover:bg-surface-low transition-colors"
            >
              Agregar productos individualmente
            </button>
          </div>
        </div>
      )}

      {/* 4. Datos del cliente (condicional) */}
      {selectedClient && (
        <SectionCard icon={<PersonIcon sx={{ fontSize: 20 }} />} title="Datos del Cliente" cols={2}>
          <CustomInput
            readOnly
            label="CUIT"
            name="clientCuit"
            value={selectedClient.cuit || ''}
            onChange={() => {}}
          />
          <CustomInput
            readOnly
            label="Domicilio"
            name="clientDomicilio"
            value={selectedClient.domicilio || ''}
            onChange={() => {}}
          />
          <CustomInput
            readOnly
            label="Localidad"
            name="clientLocalidad"
            value={selectedClient.localidad?.nombre || ''}
            onChange={() => {}}
          />
          <CustomInput
            readOnly
            label="Condición IVA"
            name="clientCondicionIva"
            value={selectedClient.condicion_IVA?.descripcion || ''}
            onChange={() => {}}
          />
        </SectionCard>
      )}

      {/* 5. Productos */}
      {selectedClient && (!isNoteType || loadMode !== null) && (
        <section className="space-y-3">
          <div className="flex items-center gap-2 px-1">
            <span className="text-blue-lahuerta">
              <ListAltIcon sx={{ fontSize: 20 }} />
            </span>
            <h2 className="text-base font-semibold text-on-surface">Productos</h2>
          </div>
          {clientWithoutPriceList && !isNoteType && (
            <p className="px-1 text-xs text-on-surface-muted">
              El cliente no tiene lista de precios: ingresá el precio de cada producto.
            </p>
          )}

          <div
            className="bg-surface-card rounded-xl shadow-sm border border-border-subtle overflow-hidden"
            data-tour="bill-items"
            data-tour-complete={isItemComplete(items[0])}
          >
            {/* Toolbar */}
            <div className="flex items-center justify-end px-4 py-3 border-b border-border-subtle">
              <button
                type="button"
                onClick={addItem}
                className="flex items-center gap-1.5 text-sm font-semibold text-blue-lahuerta hover:text-accent/80 transition-colors"
              >
                <AddCircleOutlineIcon fontSize="small" />
                Agregar línea
              </button>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-surface-low">
                    <th className="px-3 py-2.5 text-center border-b border-border-subtle w-10">
                      <span className="text-[0.6875rem] font-bold text-on-surface-muted uppercase tracking-wider">#</span>
                    </th>
                    <th className="px-3 py-2.5 text-left border-b border-border-subtle">
                      <span className="text-[0.6875rem] font-bold text-on-surface-muted uppercase tracking-wider">Producto</span>
                    </th>
                    <th className="px-3 py-2.5 text-center border-b border-border-subtle w-28">
                      <span className="text-[0.6875rem] font-bold text-on-surface-muted uppercase tracking-wider">Cantidad</span>
                    </th>
                    <th className="px-3 py-2.5 text-center border-b border-border-subtle w-36">
                      <span className="text-[0.6875rem] font-bold text-on-surface-muted uppercase tracking-wider">Tipo de venta</span>
                    </th>
                    <th className="px-3 py-2.5 text-right border-b border-border-subtle w-32">
                      <span className="text-[0.6875rem] font-bold text-on-surface-muted uppercase tracking-wider">Precio</span>
                    </th>
                    {isElectronic && (
                      <th className="px-3 py-2.5 text-center border-b border-border-subtle w-24">
                        <span className="text-[0.6875rem] font-bold text-on-surface-muted uppercase tracking-wider">IVA</span>
                      </th>
                    )}
                    <th className="px-3 py-2.5 text-right border-b border-border-subtle w-32">
                      <span className="text-[0.6875rem] font-bold text-on-surface-muted uppercase tracking-wider">Subtotal</span>
                    </th>
                    <th className="border-b border-border-subtle w-10" />
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, index) => (
                    <tr key={index} className="hover:bg-surface-low transition-colors">
                      {/* # */}
                      <td className="px-3 py-2 text-center align-middle border-b border-border-subtle text-on-surface-muted text-xs">
                        {index + 1}
                      </td>

                      {/* Producto */}
                      <td className="px-2 py-2 align-middle border-b border-border-subtle">
                        <Autocomplete
                          options={getAvailableProducts(index)}
                          value={item.producto}
                          onChange={(_, value) => handleProductSelect(index, value)}
                          size="small"
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Escribir producto..."
                              size="small"
                              sx={{
                                ...autocompleteSx(Boolean(errors[`item_${index}_producto`])),
                                minWidth: 200,
                              }}
                            />
                          )}
                        />
                      </td>

                      {/* Cantidad */}
                      <td className="px-2 py-2 align-middle border-b border-border-subtle">
                        <TextField
                          type="number"
                          size="small"
                          fullWidth
                          inputProps={{ min: 0, step: 0.01 }}
                          value={item.cantidad}
                          onChange={(event) => updateItem(index, 'cantidad', event.target.value)}
                          sx={{
                            ...autocompleteSx(Boolean(errors[`item_${index}_cantidad`])),
                            '& input': { textAlign: 'right' },
                            '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': { display: 'none' },
                            '& input[type=number]': { MozAppearance: 'textfield' },
                          }}
                        />
                      </td>

                      {/* Tipo de venta */}
                      <td className="px-2 py-2 align-middle border-b border-border-subtle">
                        <TextField
                          select
                          size="small"
                          fullWidth
                          value={item.tipo_venta ?? ''}
                          onChange={(event) =>
                            handleSaleTypeChange(
                              index,
                              event.target.value === '' ? null : Number(event.target.value)
                            )
                          }
                          sx={autocompleteSx(Boolean(errors[`item_${index}_tipo_venta`]))}
                        >
                          <MenuItem value="">-</MenuItem>
                          {saleTypes.map((saleType) => (
                            <MenuItem key={saleType.id} value={saleType.id}>
                              {saleType.descripcion}
                            </MenuItem>
                          ))}
                        </TextField>
                      </td>

                      {/* Precio */}
                      <td className="px-2 py-2 align-middle border-b border-border-subtle">
                        {isManualPrice ? (
                          <AmountInput
                            name={`precio_${index}`}
                            value={item.precio_aplicado}
                            onChange={(raw) => updateItem(index, 'precio_aplicado', raw)}
                            hasError={Boolean(errors[`item_${index}_precio`])}
                          />
                        ) : (
                          <div className="px-1 text-right text-sm text-on-surface tabular-nums">
                            {item.precio_aplicado ? formatCurrency(parseFloat(item.precio_aplicado)) : <span className="text-on-surface-muted">—</span>}
                          </div>
                        )}
                      </td>

                      {/* IVA — solo para comprobantes electrónicos */}
                      {isElectronic && (
                        <td className="px-2 py-2 align-middle border-b border-border-subtle">
                          <TextField
                            select
                            size="small"
                            fullWidth
                            value={item.iva_rate ?? '10.5'}
                            onChange={(e) => updateItem(index, 'iva_rate', e.target.value)}
                            sx={autocompleteSx(false)}
                          >
                            {IVA_OPTIONS.map((opt) => (
                              <MenuItem key={opt} value={opt}>{opt}%</MenuItem>
                            ))}
                          </TextField>
                        </td>
                      )}

                      {/* Subtotal */}
                      <td className="px-3 py-2 text-right align-middle border-b border-border-subtle text-sm font-semibold text-on-surface tabular-nums">
                        {calculateSubtotal(item) !== null
                          ? formatCurrency(calculateSubtotal(item))
                          : <span className="text-on-surface-muted font-normal">—</span>}
                      </td>

                      {/* Eliminar */}
                      <td className="px-2 py-1.5 text-center align-middle border-b border-border-subtle">
                        {items.length > 1 && (
                          <IconButton size="small" onClick={() => removeItem(index)} color="error">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Errores de items */}
            {(errors.items_empty || errors.items_duplicated) && (
              <div className="px-4 py-2 border-t border-border-subtle">
                {errors.items_empty && (
                  <p className="text-xs text-red-500">{errors.items_empty}</p>
                )}
                {errors.items_duplicated && (
                  <p className="text-xs text-red-500">{errors.items_duplicated}</p>
                )}
              </div>
            )}

            {/* Total */}
            <div className="flex justify-end items-center gap-4 px-6 py-4 border-t border-border-subtle bg-surface-low">
              <span className="text-[0.6875rem] font-bold text-on-surface-muted uppercase tracking-wider">
                Total
              </span>
              <CountUp value={total} className="text-xl font-bold text-on-surface" />
            </div>
          </div>
        </section>
      )}

      <FormActions
        onCancel={() => navigate('/bill')}
        onConfirm={handleSave}
        confirmLabel={saving ? 'Guardando…' : isEdit ? 'Guardar cambios' : 'Confirmar'}
        confirmDisabled={
          saving ||
          !items.some((item) => {
            const subtotal = calculateSubtotal(item);
            return subtotal !== null && subtotal > 0;
          })
        }
      />
    </div>
  );
};

export default FacturaForm;
